// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

/**
 * Generates a unique file name by adding a version suffix if the file name already exists
 * @param fileName - The original file name
 * @param existingFileNames - Array of existing file names to check against
 * @returns A unique file name with version suffix if needed
 */
const generateUniqueFileName = (fileName: string, existingFileNames: string[]): string => {
  // If no existing files with the same name, return the original name
  if (!existingFileNames.includes(fileName)) {
    return fileName;
  }

  // Split the file name and extension
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    // No extension, just add version suffix
    let version = 1;
    let newFileName = `${fileName}_${version}`;
    while (existingFileNames.includes(newFileName)) {
      version += 1;
      newFileName = `${fileName}_${version}`;
    }
    return newFileName;
  }

  const nameWithoutExtension = fileName.substring(0, lastDotIndex);
  const extension = fileName.substring(lastDotIndex);

  let version = 1;
  let newFileName = `${nameWithoutExtension}_${version}${extension}`;

  while (existingFileNames.includes(newFileName)) {
    version += 1;
    newFileName = `${nameWithoutExtension}_${version}${extension}`;
  }

  return newFileName;
};

export default generateUniqueFileName;

// Must mirror GetRasterTilesThunk's stem derivation (basename split at '.tif'):
// these stems are the keys of state.map.common.rasterTileJson, so any drift
// breaks the raster <-> footprint join.
export const tifStem = (filename: string): string => {
  const base = filename.split('/').pop() || filename;
  return base.split('.tif')[0];
};

export const truncateMiddle = (name: string, max: number): string => {
  if (name.length <= max) return name;
  const keep = max - 1;
  const head = Math.ceil(keep * 0.6);
  return `${name.slice(0, head)}…${name.slice(name.length - (keep - head))}`;
};

// Tile sets share a long machine prefix (timestamp, gsd, ...) and differ only
// in the tail, so map chips strip the shared part. Thresholds keep this safe:
// short prefixes are real names, and a stripped name must keep a usable tail
// (5 = more than a bare 4-char ".tif"/".laz" extension).
const MIN_SHARED_PREFIX = 10;
const MIN_DISTINCT_TAIL = 5;

export const sharedPrefixLength = (names: string[]): number => {
  if (names.length < 2) return 0;
  let prefix = names[0];
  names.slice(1).forEach((name) => {
    let i = 0;
    while (i < prefix.length && i < name.length && prefix[i] === name[i]) i += 1;
    prefix = prefix.slice(0, i);
  });
  if (prefix.length < MIN_SHARED_PREFIX) return 0;
  if (names.some((name) => name.length - prefix.length < MIN_DISTINCT_TAIL)) return 0;
  return prefix.length;
};
